import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import request from "../utils/request";
import { buildNextPath } from "../utils/authRedirect";

const deliveryTimeOptions = [
  "8:00 AM",
  "8:30 AM",
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
];

const ENTRY_TOKEN_STORAGE_PREFIX = "entry_instructions_token:";

function OrderDetailsScreen() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [entryInstructions, setEntryInstructions] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [entryToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const storageKey = `${ENTRY_TOKEN_STORAGE_PREFIX}${window.location.pathname}`;
    const urlToken = (params.get("token") || "").trim();

    if (urlToken) {
      sessionStorage.setItem(storageKey, urlToken);
      return urlToken;
    }

    return (sessionStorage.getItem(storageKey) || "").trim();
  });

  const requestConfig = useMemo(
    () =>
      entryToken
        ? {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${entryToken}`,
            },
          }
        : { withCredentials: true },
    [entryToken]
  );

  useEffect(() => {
    const nextPath = buildNextPath(location);
    const currentPath = `${location.pathname}${location.search}${location.hash}`;

    if (currentPath !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await request.get(`/orders/${orderId}/entry-instructions`, requestConfig);

        if (!response.data.success) {
          setError(response.data.message || "Failed to load order details.");
          return;
        }

        setOrderData(response.data);
        setEntryInstructions(response.data.order?.entryInstructions || "");
        setDeliveryTime(response.data.order?.hostSetDeliveryTime || "");
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError(err.response?.data?.message || "Failed to load order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, requestConfig]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!entryInstructions.trim()) {
      setError("Entry instructions are required.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccessMessage("");

      const response = await request.patch(
        `/orders/${orderId}/entry-instructions`,
        {
          entryInstructions: entryInstructions.trim(),
          deliveryTime,
        },
        requestConfig
      );

      if (!response.data.success) {
        setError(response.data.message || "Failed to save entry instructions.");
        return;
      }

      setOrderData((current) => ({
        ...(current || {}),
        canEdit: response.data.canEdit,
        order: response.data.order,
        property: response.data.property,
      }));
      setEntryInstructions(response.data.order?.entryInstructions || "");
      setDeliveryTime(response.data.order?.hostSetDeliveryTime || "");
      setSuccessMessage(response.data.message || "Entry instructions saved.");
    } catch (err) {
      console.error("Error submitting entry instructions:", err);
      if (err.response?.status === 409) {
        setOrderData((current) =>
          current ? { ...current, canEdit: false } : current
        );
      }
      setError(err.response?.data?.message || "Failed to save entry instructions.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const canEdit = orderData?.canEdit !== false;
  const order = orderData?.order;
  const property = orderData?.property;

  // Optional AvantStay-only names. Joined for display only; rows are omitted
  // entirely when guestName is absent so legacy orders render unchanged.
  const joinName = (...parts) =>
    parts
      .map((part) => (typeof part === "string" ? part.trim() : ""))
      .filter(Boolean)
      .join(" ");
  const reservationHolderName = joinName(
    order?.guestName?.reservationHolderFirstName,
    order?.guestName?.reservationHolderLastName
  );
  const orderPlacedByName = joinName(
    order?.guestName?.orderPlacedByFirstName,
    order?.guestName?.orderPlacedByLastName
  );
  if (loading) {
    return (
      <div className="min-h-full bg-[#FAFAFA] px-6 py-8">
        <div className="mx-auto max-w-4xl rounded-[18px] border border-[#EAEAEA] bg-white p-10 text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-[#111111]" />
          <p className="text-[13px] font-semibold text-[#5F6368]">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="min-h-full bg-[#FAFAFA] px-6 py-8">
        <div className="mx-auto max-w-4xl rounded-[18px] border border-[#EAEAEA] bg-white px-6 py-10 text-center">
          <h2 className="text-[20px] font-extrabold text-[#111111]">Unable to load this order</h2>
          <p className="mt-2 text-[13px] text-[#5F6368]">{error || "Order details are unavailable."}</p>
          <div className="mt-6">
            <Link
              to="/order-list"
              className="inline-flex items-center h-9 px-4 rounded-[10px] bg-[#111111] text-[12px] font-bold text-white transition hover:bg-black"
            >
              Back to Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#FAFAFA] px-6 py-6">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1">
              Host Access
            </p>
            <h1 className="text-[26px] font-extrabold leading-tight text-[#111111]" style={{ letterSpacing: -0.5 }}>
              Entry Instructions
            </h1>
            <p className="mt-0.5 text-[13px] text-[#5F6368]">
              Review and update access details for this order.
            </p>
          </div>
          <Link
            to="/order-list"
            className="inline-flex shrink-0 items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:text-[#111111] hover:border-[#111111]"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Orders
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-[1.05fr_1fr]">
          <div className="rounded-[18px] border border-[#EAEAEA] bg-white p-5">
            <p className="mb-4 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">Order Details</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Order ID</p>
                <p className="text-[14px] font-bold text-[#111111]">#{order?.uniqueId ?? "—"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Status</p>
                <p className="text-[14px] font-bold text-[#111111]">{order?.status || "N/A"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Guest Email</p>
                <p className="break-all text-[14px] font-bold text-[#111111]">{order?.guestEmail || "N/A"}</p>
              </div>
              {reservationHolderName ? (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Reservation Holder</p>
                  <p className="text-[14px] font-bold text-[#111111]">{reservationHolderName}</p>
                </div>
              ) : null}
              {orderPlacedByName ? (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Order Placed By</p>
                  <p className="text-[14px] font-bold text-[#111111]">{orderPlacedByName}</p>
                </div>
              ) : null}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Delivery Date</p>
                <p className="text-[14px] font-bold text-[#111111]">{formatDate(order?.inStockDate)}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">Delivery Address</p>
                <p className="text-[14px] font-bold leading-snug text-[#111111]">
                  {property?.address?.line1}
                  {property?.address?.line2 ? <><br />{property.address.line2}</> : null}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-[18px] border border-[#EAEAEA] bg-white p-5">
            <p className="mb-3 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">Current Access Notes</p>
            <p className="whitespace-pre-wrap text-[13px] leading-6 text-[#111111]">
              {entryInstructions || "No entry instructions saved yet."}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-[18px] border border-[#EAEAEA] bg-white p-5"
        >
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-[16px] font-extrabold text-[#111111]">
                {canEdit ? "Update Entry Instructions" : "Entry Instructions Locked"}
              </h2>
              <p className="mt-0.5 text-[12px] text-[#888888]">
                {canEdit
                  ? "Changes save directly to the order and property access notes."
                  : "Completed and cancelled orders are read-only."}
              </p>
            </div>
            {!canEdit && (
              <span className="inline-flex rounded-full bg-[#F5F5F5] border border-[#EAEAEA] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#5F6368]">
                Read only
              </span>
            )}
          </div>

          {error && (
            <div className="mb-3 rounded-[10px] border border-red-100 bg-red-50 px-3 py-2.5">
              <p className="text-[13px] font-semibold text-red-600">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-3 rounded-[10px] border border-[#D1FAE5] bg-[#ECFDF5] px-3 py-2.5">
              <p className="text-[13px] font-semibold text-[#047857]">{successMessage}</p>
            </div>
          )}

          {!canEdit && (
            <div className="mb-3 rounded-[10px] border border-amber-100 bg-amber-50 px-3 py-2.5">
              <p className="text-[13px] font-semibold text-amber-700">
                This order can still be reviewed here, but edits are no longer allowed.
              </p>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label htmlFor="entryInstructions" className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">
                Entry Instructions <span className="text-red-500">*</span>
              </label>
              <textarea
                id="entryInstructions"
                value={entryInstructions}
                onChange={(event) => {
                  setEntryInstructions(event.target.value);
                  if (successMessage) setSuccessMessage("");
                }}
                rows={6}
                required
                disabled={!canEdit || submitting}
                className="w-full resize-y rounded-[10px] border border-[#EAEAEA] bg-white px-3.5 py-2.5 text-[13px] text-[#111111] focus:outline-none focus:border-[#111111] disabled:cursor-not-allowed disabled:bg-[#F5F5F5]"
                placeholder="e.g., Keypad code is 1234, park in spot #7, enter through side door..."
              />
              <p className="mt-1 text-[12px] text-[#888888]">
                This updates both the order notes and the saved property access description.
              </p>
            </div>

            <div>
              <label htmlFor="deliveryTime" className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.14em] text-[#888888]">
                Delivery Time (when house cleaned)
              </label>
              <select
                id="deliveryTime"
                value={deliveryTime}
                onChange={(event) => {
                  setDeliveryTime(event.target.value);
                  if (successMessage) setSuccessMessage("");
                }}
                disabled={!canEdit || submitting}
                className="w-full rounded-[10px] border border-[#EAEAEA] bg-white px-3.5 py-2.5 text-[13px] text-[#111111] focus:outline-none focus:border-[#111111] disabled:cursor-not-allowed disabled:bg-[#F5F5F5]"
              >
                <option value="">Select a time</option>
                {deliveryTimeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[12px] text-[#888888]">
                Leave this blank if Bellhopt should continue without a host-set delivery time.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={submitting || !canEdit || !entryInstructions.trim()}
              className="inline-flex items-center h-10 px-5 rounded-[10px] bg-[#111111] text-[13px] font-bold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Save Entry Instructions"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrderDetailsScreen;
